import { useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { Circle, RecordCircle, TrashFill } from "react-bootstrap-icons";
import { Typeahead, type TypeaheadRef } from "react-bootstrap-typeahead";
import type { AssignedAttorney } from "@/briefpoint-client";
import "./Attorneys.scss";
import Avatar from "react-initials-avatar";
import type { UserResponse } from "@/lib/types";

interface Props {
  assignedAttorneys: AssignedAttorney[];
  topLevelAttorneys: AssignedAttorney[];
  users: UserResponse[] | null;
  setAttorneys: React.Dispatch<React.SetStateAction<AssignedAttorney[]>>;
}

export type Option = string | Record<string, unknown>;

export default function Attorneys({
  users,
  assignedAttorneys,
  topLevelAttorneys,
  setAttorneys,
}: Props) {
  const [addAttorneyId, setAddAttorneyId] = useState("");
  const typeaheadRef = useRef<TypeaheadRef>(null);
  const caseHasPrimary = !!assignedAttorneys.find((x) => x.isPrimary);
  const clientAttorneys = caseHasPrimary
    ? []
    : users?.filter((u) => topLevelAttorneys.find((a) => a.id === u.id));
  const attorneysToAdd = users?.filter(
    (u) =>
      !assignedAttorneys?.find((a) => a.id === u.id) &&
      !clientAttorneys?.find((a) => a.id === u.id),
  );

  function addAttorney() {
    if (addAttorneyId) {
      setAttorneys((a) => [
        ...a,
        {
          id: addAttorneyId,
          isPrimary: !a.length && !topLevelAttorneys?.find((x) => x.isPrimary),
          order: a.length,
        },
      ]);
      setAddAttorneyId("");
      typeaheadRef?.current?.clear();
    }
  }

  function removeAttorney(attorney: AssignedAttorney) {
    let attorneysTemp = assignedAttorneys.filter((x) => x !== attorney);

    if (attorneysTemp.length !== assignedAttorneys.length) {
      if (
        attorneysTemp.length &&
        !topLevelAttorneys?.find((x) => x.isPrimary)
      ) {
        attorneysTemp[0].isPrimary = true;
      }
      if (attorney.isPrimary && topLevelAttorneys?.length) {
        attorneysTemp = attorneysTemp.filter(
          (x) => !topLevelAttorneys?.find((t) => t.id === x.id),
        );
      }
      setAttorneys(attorneysTemp);
    }
  }

  function makePrimary(attorney: AssignedAttorney) {
    const attorneysTemp = [...assignedAttorneys];
    const primary = attorneysTemp.find((x) => x.isPrimary);
    if (primary) {
      primary.isPrimary = false;
    }
    const update = attorneysTemp.find((x) => x.id === attorney.id);
    if (update) {
      update.isPrimary = true;
    }

    setAttorneys(attorneysTemp);
  }

  function removePrimary(attorney: AssignedAttorney) {
    // If there isn't a toplevel (from Client) attorney that's primary, you can only swap primary's
    if (!topLevelAttorneys?.find((x) => x.id)) {
      return;
    }

    let attorneysTemp = [...assignedAttorneys];
    const primary = attorneysTemp.find((x) => x.id === attorney.id);
    if (primary?.isPrimary) {
      primary.isPrimary = false;
      if (topLevelAttorneys?.length) {
        attorneysTemp = attorneysTemp.filter(
          (x) => !topLevelAttorneys?.find((t) => t.id === x.id),
        );
      }
    } else {
      return;
    }

    setAttorneys(attorneysTemp);
  }

  return (
    <>
      {users && users.length > 1 && (
        <div id="attorneys-container">
          <h3>Attorneys</h3>
          {attorneysToAdd && !!attorneysToAdd.length && (
            <Row className="add-attorneys">
              <Col className="col-6">
                <Typeahead
                  onChange={(selected) =>
                    setAddAttorneyId((selected[0] as UserResponse)?.id ?? "")
                  }
                  clearButton
                  ref={typeaheadRef}
                  id="attorney-select"
                  options={attorneysToAdd || []}
                  placeholder="Select an Attorney..."
                  labelKey={(o) =>
                    `${(o as UserResponse).firstName} ${(o as UserResponse).lastName}`
                  }
                />
              </Col>
              <Col className="col-3">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={addAttorney}
                  disabled={!addAttorneyId}
                >
                  + Assign Attorney
                </Button>
              </Col>
            </Row>
          )}
          <Row className="headers">
            <Col className="col-6">Attorney Name</Col>
            <Col className="col-3">Primary Attorney</Col>
            <Col className="col-1" />
          </Row>
          {!caseHasPrimary &&
            !!topLevelAttorneys.length &&
            topLevelAttorneys?.map((a) => {
              const u = users.find((x) => x.id === a.id);
              const name = `${u?.firstName} ${u?.lastName} (From Client)`;
              return (
                <Row className="top-level" key={`top-${a.id}`}>
                  <Col className="bold col-6">
                    <Avatar className="me-2" name={name} />
                    {name}
                  </Col>
                  <Col className="col-3">
                    {a.isPrimary ? <RecordCircle /> : <Circle />}
                  </Col>
                  <Col className="col-1" />
                </Row>
              );
            })}
          {assignedAttorneys?.map((a) => {
            const u = users.find((x) => x.id === a.id);
            const name = `${u?.firstName} ${u?.lastName}`;
            return (
              <Row className="attorney-select" key={`this-${a.id}`}>
                <Col className="bold col-6">
                  <Avatar className="me-2" name={name} />
                  {name}
                </Col>
                <Col className="set-primary col-3">
                  {a.isPrimary ? (
                    <RecordCircle
                      size={"20px"}
                      onClick={() => removePrimary(a)}
                    />
                  ) : (
                    <Circle size={"20px"} onClick={() => makePrimary(a)} />
                  )}
                </Col>
                <Col className="col-1">
                  <TrashFill
                    onClick={() => removeAttorney(a)}
                    className="table-edit-item"
                    title="Unassign"
                  />
                </Col>
              </Row>
            );
          })}
        </div>
      )}
    </>
  );
}
